/* Photos.js */

import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { Row, Col, Modal, ModalBody, Button, UncontrolledButtonDropdown, DropdownToggle, DropdownMenu, DropdownItem, ButtonGroup, ModalHeader } from 'reactstrap';
import { useAuth } from '../../../../Context/Auth/AuthContext';
import { toast } from 'react-toastify';
import PSO from '../../../../services/PSO';
import Strings from '../../../../translations/Translations';
import MediaStatuses from '../../../../enums/MediaStatuses';
import AppleStoreImage from '../../../../assets/utils/images/apple_pp_store-267x89.png';
import PlayStoreImage from '../../../../assets/utils/images/google-play-badge-300x89.png';
import ModalAddPhoto from '../../../../Layout/Components/ModalAddPhoto';
import MediaThumb1 from '../../../../Layout/Components/MediaThumb1';

const IMAGE_SRC = process.env.REACT_APP_PZ_IMAGE_SRC;
const VIDEO_SRC = process.env.REACT_APP_PZ_VIDEO_SRC;
const REMIX_APP_IOS_LINK = process.env.REACT_APP_REMIX_APP_IOS_LINK;
const REMIX_APP_ANDROID_LINK = process.env.REACT_APP_REMIX_APP_ANDROID_LINK;

const DEFAULT_ESORT = 2;
const defaultEnmax = 2;

export default function Photos(props) {
  const { currentIt, addPhotoRef } = props;

  const { authenticated, toggleLoading } = useAuth();
  const [photos, setPhotos] = useState([]);
  const [currentPhoto, setCurrentPhoto] = useState();
  const [currentFilter, setCurrentFilter] = useState(MediaStatuses[0]);
  
  const [showLoadMore, setShowLoadMore] = useState(false);
  const [lastMedia, setLastMedia] = useState();
  const [isLoading, setIsLoading] = useState(false);

  const [photoModal, setPhotoModal] = useState(false);
  const togglePhotoModal = useCallback(() => {
    setPhotoModal(!photoModal);
  }, [setPhotoModal, photoModal]);

  const [modalAddPhoto, setModalAddPhoto] = useState(false);
  const toggleModalAddPhoto = useCallback(() => {
    setModalAddPhoto(!modalAddPhoto)
  }, [setModalAddPhoto, modalAddPhoto]);

  const getPhotos = useCallback(async (key = 0) => {
    setIsLoading(true);

    try {
      let response;

      if (key === 0) {
        response = await PSO.getRemixApprovedElements({
          un: authenticated.un,
          al: authenticated.al,
          rid: currentIt.rid,
          enmax: defaultEnmax,
          esort: DEFAULT_ESORT
        });
      } else if (key === 1) {
        response = await PSO.getRemixNonApprovedElements({
          un: authenticated.un,
          al: authenticated.al,
          rid: currentIt.rid,
          enmax: defaultEnmax,
          estatus: 6
        });
      } else if (key === 2) {
        response = await PSO.getRemixNonApprovedElements({
          un: authenticated.un,
          al: authenticated.al,
          rid: currentIt.rid,
          enmax: defaultEnmax,
          estatus: 5
        });
      } else if (key === 3) {
        response = await PSO.getStickyElementIDListInRemix({
          un: authenticated.un,
          al: authenticated.al,
          rid: currentIt.rid,
          enmax: defaultEnmax,
          etype: 1
        });
      }

      if (response.result === 'success') {
        if (response.its_media) {
          const media = response.its_media.map((value) => {
            if (key === 0) {
              value.to_authorize = 0;
            } else {
              value.to_authorize = 1;
            }
            return value;
          });
          setPhotos(media);
          
          setShowLoadMore(media && media.length === defaultEnmax);
        } else if (response.its) {
          setPhotos(response.its);
        }

        setLastMedia({
          eidul: response.lastmediaeidul,
          esticknul: response.lastmediaesticknul,
          esvul: response.lastmediaesvul
        });
      } else {
        const message = (response && response.error) ? response.error : Strings('error_on_request');
        toast.error(message);
      }

      setIsLoading(false);
    } catch (err) {
      toast.error(Strings('general_error'));
      setIsLoading(false);
    }
  }, [currentIt, authenticated, setPhotos]);

  const getMorePhotos = useCallback(async (key = 0) => {
    if (currentIt && currentIt.rid) {
      toggleLoading(true);

      try {
        let response;

        const pars = {
          un: authenticated.un,
          al: authenticated.al,
          rid: currentIt.rid,
          enmax: defaultEnmax,
          esort: DEFAULT_ESORT
        };

        if (lastMedia) {
          pars.eidul = lastMedia.eidul;
          pars.esticknul = lastMedia.esticknul;
          pars.esvul = lastMedia.esvul;
        }

        if (key === 0) {
          response = await PSO.getRemixApprovedElements(pars);
        } else if (key === 1) {
          pars.estatus = 6;
          response = await PSO.getRemixNonApprovedElements(pars);
        } else if (key === 2) {
          pars.estatus = 5;
          response = await PSO.getRemixNonApprovedElements(pars);
        } else if (key === 3) {
          pars.etype = 1;
          response = await PSO.getStickyElementIDListInRemix(pars);
        }

        if (response.result === 'success') {
          if (response.its_media) {
            const media = response.its_media.map((value) => {
              if (key === 0) {
                value.to_authorize = 0;
              } else {
                value.to_authorize = 1;
              }
              return value;
            });
            
            setPhotos([...photos, ...media]);
            
            const isToShowLoadMore = (response.its_media.length === defaultEnmax);
            setShowLoadMore(isToShowLoadMore);
          } else if (response.its) {
            setPhotos([...photos, ...response.its]);
          }

          setLastMedia({
            eidul: response.lastmediaeidul,
            esticknul: response.lastmediaesticknul,
            esvul: response.lastmediaesvul
          });
        } else {
          const message = (response && response.error) ? response.error : Strings('error_on_request');
          toast.error(message);
        }
      } catch (err) {
        toast.error(Strings('general_error'));
      }

      toggleLoading(false);
    }
  }, [currentIt, authenticated, photos, setPhotos, setShowLoadMore, toggleLoading, lastMedia]);

  const applyFilter = useCallback(async (value) => {
    if (value.key !== currentFilter.key) {
      setPhotos([]);
      setCurrentFilter(value);
      setShowLoadMore(false);
      setLastMedia();
      getPhotos(value.key);
    }
  }, [setCurrentFilter, currentFilter, getPhotos, setPhotos]);

  const disapproveElement = useCallback(async (eid, etype) => {
    toggleLoading(true);

    try {
      const response = await PSO.disapproveElementInRemix({
        un: authenticated.un,
        al: authenticated.al,
        rid: currentIt.rid,
        eid,
        etype
      });

      if (response.result === 'success') {
        // Remover o elemento da lista local em vez de recarregar tudo
        setPhotos(photos.filter((value) => value.eid !== eid));
      } else {
        const message = (response && response.error) ? response.error : Strings('error_on_request');
        toast.error(message);
      }

      toggleLoading(false);
    } catch (err) {
      toast.error(Strings('general_error'));
      toggleLoading(false);
    }
    togglePhotoModal();
  }, [authenticated, currentIt, toggleLoading, photos, setPhotos, togglePhotoModal]);

  const approveElement = useCallback(async (eid, etype) => {
    toggleLoading(true);

    try {
      const response = await PSO.approveElementInRemix({
        un: authenticated.un,
        al: authenticated.al,
        rid: currentIt.rid,
        eid,
        etype
      });

      if (response.result === 'success') {
        // Recarregar a lista para atualizar o status
        getPhotos(currentFilter.key);
      } else {
        const message = (response && response.error) ? response.error : Strings('error_on_request');
        toast.error(message);
      }

      toggleLoading(false);
    } catch (err) {
      toast.error(Strings('general_error'));
      toggleLoading(false);
    }
    togglePhotoModal();
  }, [authenticated, currentIt, toggleLoading, getPhotos, currentFilter, togglePhotoModal]);

  useEffect(() => {
    getPhotos();
  }, [getPhotos]);

  return (
    <Fragment>
      <Row className='mb-3'>
        <Col>
          <UncontrolledButtonDropdown>
            <DropdownToggle caret color="light">
              {currentFilter.label}
            </DropdownToggle>
            <DropdownMenu>
              <DropdownItem header>{Strings('filter_photos')}</DropdownItem>
              {MediaStatuses.map((value) => (
                <DropdownItem
                  key={value.key}
                  onClick={() => applyFilter(value)}
                >
                  {value.label}
                </DropdownItem>
              ))}
            </DropdownMenu>
          </UncontrolledButtonDropdown>
        </Col>
        <Col className='text-right'>
          <Button
            color='primary'
            onClick={toggleModalAddPhoto}
            innerRef={addPhotoRef}
          >
            {Strings('add_photo')}
          </Button>
        </Col>

        <ModalAddPhoto
          modalOpen={modalAddPhoto}
          toggleModal={toggleModalAddPhoto}
          rid={currentIt.rid}
          callback={getPhotos}
        />
      </Row>
      <Row>
        {photos && photos.length === 0 && currentFilter.key !== 3 &&  (
          <Col>
            <div className="mt-2 alert alert-info fade show" role="alert">
              {Strings('no_photos_here')}
            </div>
          </Col>
        )}

        {currentFilter.key === 3 && (
          <Col>
            <div className="mt-2 alert alert-info fade show" role="alert">
              <p>
                {Strings('manage_sticky_photos')}
              </p>
              <p>
                <a href={REMIX_APP_IOS_LINK} target="_blank" rel="noopener noreferrer">
                  <img src={AppleStoreImage} className="mr-2 get_app_image_button" alt={Strings('download_remix_app_for_ios')} />
                </a>

                <a href={REMIX_APP_ANDROID_LINK} target="_blank" rel="noopener noreferrer">
                  <img src={PlayStoreImage} className="get_app_image_button" alt={Strings('download_remix_app_for_android')} />
                </a>
              </p>
            </div>
          </Col>
        )}

        <Col sm={12}>
          {photos && (
            <Row>
              {photos && photos.map((value, index) => (
                <Col sm={12} md={2} lg={2} key={value.eid}>
                  <MediaThumb1
                    photo={value}
                    callback={() => {
                      setCurrentPhoto(value);
                      togglePhotoModal();
                    }}
                  />
                </Col>
              ))}
            </Row>
          )}
        </Col>

        {isLoading && !showLoadMore && (
          <Col sm={12} className='d-flex justify-content-center mt-3'>
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Loading...</span>
            </div>
          </Col>
        )}

        {showLoadMore && photos && (
          <Col sm={12} className='d-flex justify-content-center mt-3'>
            <Button
              color='primary'
              size='sm'
              onClick={() => getMorePhotos(currentFilter.key)}
              disabled={isLoading}
            >
              {isLoading ? 'Loading...' : Strings('load_more')}
            </Button>
          </Col>
        )}

        <Modal isOpen={photoModal} toggle={togglePhotoModal} size='lg'>
          <ModalHeader toggle={togglePhotoModal} />
          <ModalBody>
            {currentPhoto && (
              <Row>
                <Col md={12} className="text-center">
                  {parseInt(currentPhoto.etype) === 1 && (
                    <img
                      className="img-fluid"
                      alt={currentPhoto.eid}
                      src={IMAGE_SRC.replace('{id}', currentPhoto.eid)}
                    />
                  )}
                  {parseInt(currentPhoto.etype) === 2 && (
                    <video
                      playsInline
                      controls
                      controlsList='nodownload noplaybackrate noremoteplayback'
                      disablePictureInPicture
                    >
                      <source src={VIDEO_SRC.replace('{id}', currentPhoto.eid)} />
                    </video>
                  )}
                  {/* <img
                    className="img-fluid img-media-manage-modal"
                    alt=""
                    src={IMAGE_SRC.replace('{id}', currentPhoto.eid)}
                    height="300"
                  /> */}
                </Col>
                <Col md={12} className='text-center'>
                  <p className='manage-media-modal-user mb-1'>
                    @{currentPhoto.eown}
                  </p>

                  <ButtonGroup size="md">
                    {currentPhoto.to_authorize === 0 && (
                      <Button
                        color="danger"
                        className="m-1"
                        outline
                        onClick={() => disapproveElement(currentPhoto.eid, currentPhoto.etype)}
                      >
                        {Strings('disapprove')}
                      </Button>
                    )}
                    {currentPhoto.to_authorize === 1 && (
                      <Button
                        color="success"
                        className="m-1"
                        outline
                        onClick={() => approveElement(currentPhoto.eid, currentPhoto.etype)}
                      >
                        {Strings('approve')}
                      </Button>
                    )}
                  </ButtonGroup>
                </Col>

              </Row>
            )}
          </ModalBody>
        </Modal>
      </Row>
    </Fragment>
  );
}
